import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  gql,
  useMutation,
  useQuery,
  useNavigate,
  useParams,
  useLocation,
} from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import { Button, Col, Modal, Row, Space, Typography } from 'antd';
import { StaffingRequestAssignmentPageHeader } from 'works/components/staffing-request-assignment-page-header';
import {
  CandidateOfferListItem,
  CandidateSubmissionsList,
} from '@applicant-flow/components';
import {
  AppContent,
  ErrorHandler,
  Icon,
  Link,
  LoadingState,
  Tabs,
  TextOverflow,
  WorksAvatar,
} from '@common/components';
import { DateOfBirthLink, SsnLink } from '@clinician-profile/components';
import { Colors, Spacing } from 'works/legacy/lib/style-helper';
import CandidateProfileTab from './candidate-profile-tab';
import { SubmittalPacketsManagementDrawerButton } from 'works/components/submittal-packets-management-drawer-button';
import { mutationResponseMessage } from 'works/legacy/lib/message-helper';
import { BillingReverseInvoiceList } from '@pay-bill/components/billing-reverse-invoice-list';
import { SubmittalPacketInfoFragment } from 'works/graphql/submittal-packet';
import {
  OFFER_CONFIRMATION_DRAWER_INFO_FRAGMENT,
  STAFFING_REQUEST_APPLICATION_INFO_FRAGMENT,
} from '@applicant-flow/graphql';
import { buildDialogUrl } from '@common/services';

const { Text, Title } = Typography;

const WORKS_PROFILE_QUERY = gql`
  query supplierDetailedCandidateProfile($id: ID!) {
    worksProfile(id: $id) {
      id
      firstName
      lastName
      fullName
      phone
      email
      profileSummary
      profileType
      canArchive {
        value
      }
      role {
        id
        name
      }
      primarySpecialty {
        id
        displayName
      }
      secondarySpecialties {
        id
        displayName
      }

      ...SubmittalPacketFragment

      canShowPhi {
        value
      }

      staffingRequestApplications {
        id
        ...StaffingRequestApplicationInfo

        worksProfile {
          id
          fullName
        }
      }

      sortedOffers {
        id
        staffingRequestApplication {
          id
        }
        ...OfferConfirmationDrawerInfo
        ...OfferListInfo
      }

      currentStaffingRequestAssignment {
        id
        ...StaffingRequestAssignmentPageHeaderInfo
      }

      licenses {
        id
        state
        friendlyLicenseType
        number
        expiresOn
      }
    }
  }

  ${StaffingRequestAssignmentPageHeader.fragments.headerInformation}
  ${OFFER_CONFIRMATION_DRAWER_INFO_FRAGMENT}
  ${STAFFING_REQUEST_APPLICATION_INFO_FRAGMENT}
  ${CandidateOfferListItem.fragments.OfferListInfo}
  ${SubmittalPacketInfoFragment}
`;

const SUPPLIER_ARCHIVE_CANDIDATE_MUTATION = gql`
  mutation supplierArchiveCandidateMutation($id: ID!) {
    archiveWorksProfile(id: $id) {
      worksProfile {
        id
        fullName
      }

      errors {
        attribute
        humanAttribute
        errors
        fullErrors
      }
    }
  }
`;

export function SupplierCandidateProfile({ worksProfileId }) {
  const [showArchiveConfirmationModal, setShowArchiveConfirmationModal] =
    useState(false);
  const location = useLocation();
  const { tabKey } = useParams();
  const [activeTab, setActiveTab] = useState(tabKey);
  const navigate = useNavigate();
  const currentMembership = useCurrentMembership();

  const { loading, error, data } = useQuery(WORKS_PROFILE_QUERY, {
    variables: { id: worksProfileId },
  });

  const [archiveCandidate, { loading: mutationLoading }] = useMutation(
    SUPPLIER_ARCHIVE_CANDIDATE_MUTATION
  );

  const handleArchiveClick = () => {
    setShowArchiveConfirmationModal(true);
  };

  const handleArchiveConfirm = () => {
    if (!mutationLoading) {
      archiveCandidate({
        variables: {
          id: worksProfileId,
        },
      }).then((response) => {
        const errors = response.errors;

        mutationResponseMessage({
          gqlResponse: response,
          onSuccess: 'Candidate successfully archived',
          errorMessage: 'Failed to archive candidate',
        });

        if (errors) {
          setShowArchiveConfirmationModal(false);
        } else {
          navigate('/works/supplier/candidates');
        }
      });
    }
  };

  if (loading && !data) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorHandler error={error} log="SupplierCandidateProfile" />;
  }

  const worksProfile = data.worksProfile;

  const secondarySpecialties = worksProfile.secondarySpecialties
    .map((specialty) => specialty.displayName)
    .join(', ');

  const updateCandidateDrawerUrl = buildDialogUrl('update-candidate-drawer', {
    props: { worksProfileId },
  });

  return (
    <AppContent>
      <section className="profile-header">
        <Row>
          <Row align="middle" style={{ flexGrow: 1 }}>
            <WorksAvatar
              avatarImageSrc={worksProfile.user && worksProfile.profilePic}
              firstName={worksProfile.firstName}
              lastName={worksProfile.lastName}
              size={112}
            />

            <div>
              <Row align="middle">
                <Title level={3} className="profile-title sentry-mask">
                  {worksProfile.fullName}
                </Title>
                {worksProfile.profileType === 'international' && (
                  <span className="profile-international">International</span>
                )}
              </Row>

              <Row gutter={[32, 0]} className="sentry-mask">
                <Col>
                  <ul className="profile-contact fa-ul">
                    {worksProfile.phone && (
                      <li>
                        <span className="fa-li">
                          <Icon
                            icon="mobile"
                            kind="light"
                            tooltipText="Mobile Number"
                          />
                          {'\u00A0'}
                        </span>
                        <span>{worksProfile.phone}</span>
                      </li>
                    )}

                    {worksProfile.email && (
                      <li className="sentry-mask">
                        <span className="fa-li">
                          <Icon
                            icon="envelope"
                            kind="light"
                            tooltipText="Email"
                          />
                          {'\u00A0'}
                        </span>
                        <span>{worksProfile.email}</span>
                      </li>
                    )}

                    {worksProfile?.canShowPhi.value && (
                      <li className="sentry-mask">
                        <span className="fa-li">
                          <Icon
                            icon="birthdayCake"
                            kind="light"
                            tooltipText="Birthdate"
                          />
                          {'\u00A0'}
                        </span>
                        <span>
                          <DateOfBirthLink worksProfileId={worksProfile.id} />
                        </span>
                      </li>
                    )}
                    {worksProfile?.canShowPhi.value && (
                      <li className="sentry-mask">
                        <span className="fa-li">
                          <Icon
                            icon="idCard"
                            kind="light"
                            tooltipText="Social Security Number"
                          />
                          {'\u00A0'}
                        </span>
                        <span>
                          <SsnLink
                            worksProfileId={worksProfile.id}
                            canShowPhi={worksProfile.canShowPhi.value}
                          />
                        </span>
                      </li>
                    )}
                  </ul>
                </Col>
                <Col>
                  <ul className="profile-contact fa-ul">
                    <li>
                      <span className="fa-li">
                        <Icon
                          icon="userNurse"
                          kind="light"
                          tooltipText="Role"
                        />
                        {'\u00A0'}
                      </span>
                      {worksProfile.role?.name ? (
                        <span>{worksProfile.role.name}</span>
                      ) : (
                        <Link to={updateCandidateDrawerUrl}>Add Role</Link>
                      )}
                    </li>
                    <li>
                      <span className="fa-li">
                        <Icon
                          icon="bedPulse"
                          kind="light"
                          tooltipText="Specialty"
                        />
                        {'\u00A0'}
                      </span>
                      {worksProfile.primarySpecialty?.displayName ? (
                        <span>{worksProfile.primarySpecialty.displayName}</span>
                      ) : (
                        <Link to={updateCandidateDrawerUrl}>Add Specialty</Link>
                      )}
                    </li>
                    <li>
                      {secondarySpecialties.length < 70 ? (
                        <span>{secondarySpecialties}</span>
                      ) : (
                        <TextOverflow tooltip={secondarySpecialties}>
                          {`${secondarySpecialties.slice(0, 70)}...`}
                        </TextOverflow>
                      )}
                    </li>
                  </ul>
                </Col>
              </Row>
            </div>
          </Row>

          <Space align="top">
            {worksProfile.canArchive.value && (
              <>
                <Button onClick={handleArchiveClick} ghost danger>
                  Archive
                </Button>
                <Modal
                  visible={showArchiveConfirmationModal}
                  okText="Confirm"
                  onOk={handleArchiveConfirm}
                  onCancel={() => setShowArchiveConfirmationModal(false)}
                >
                  <Title level={4}>
                    Are you sure you want to archive this candidate?
                  </Title>
                  <Text type="secondary">
                    After archiving, this candidate will not be listed
                  </Text>
                </Modal>
              </>
            )}

            <Link to={updateCandidateDrawerUrl}>
              <Button>Edit Profile</Button>
            </Link>
          </Space>
        </Row>
      </section>
      <Tabs
        activeKey={activeTab}
        onTabClick={(key) => {
          const updatedUrl = location.pathname.replace(
            `/${activeTab}`,
            `/${key}`
          );

          navigate(updatedUrl, { replace: true });
          setActiveTab(key);
        }}
      >
        <Tabs.Pane tab="submissions" key="submissions">
          <div className="profile__submittal-packets">
            <SubmittalPacketsManagementDrawerButton
              worksProfile={worksProfile}
            />
          </div>
          <CandidateSubmissionsList worksProfileId={worksProfile.id} />
        </Tabs.Pane>
        <Tabs.Pane tab="profile" key="profile" className="profile-tab">
          <CandidateProfileTab worksProfile={worksProfile} />
        </Tabs.Pane>
        {currentMembership.canShowReverseInvoices && (
          <Tabs.Pane tab="reverse invoices" key="reverse_invoices">
            <BillingReverseInvoiceList
              hiddenFilters={{ worksProfile: [worksProfile.id] }}
            />
          </Tabs.Pane>
        )}
      </Tabs>
      <style jsx>{`
        .candidate-title-section {
          flex-grow: 1;
          margin-right: 1rem;
        }
        .profile-header {
          margin-bottom: 2rem;
        }
        .profile-international {
          margin-left: ${Spacing.baseSpacing / 4}px;
          color: ${Colors.steelDarkest};
        }
        section :global(.profile-title) {
          margin: 0 16px;
        }
        .profile-titles {
          font-size: 18px;
        }
        .profile-contact {
          line-height: 2;
          margin-top: 0.5rem;
          margin-left: 38px;
        }
        .profile__submittal-packets {
          display: flex;
          justify-content: flex-end;
          margin: 24px 0;
        }
        :global(.profile-tab) {
          margin-top: ${Spacing.baseSpacingPx};
        }
      `}</style>
    </AppContent>
  );
}

SupplierCandidateProfile.propTypes = {
  worksProfileId: PropTypes.string.isRequired,
};
